/* eslint-disable no-empty */
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { setYear } from 'date-fns'
import { Alpha3Code } from 'i18n-iso-countries'
import { useCallback, useEffect, useMemo, useReducer } from 'react'

import { AppRoute } from '../../../routing/AppRoute.enum'
import {
  getSsoLoginUrl,
  responseFailureInterceptor,
  responseSuccessInterceptor
} from '../../client/clientContextController/interceptors/responseInterceptors'
import { BASE_PALETTE } from '../../colorTheme/colorThemeContextController/ColorThemeContextController'
import { AuthStateContext } from '../authContext/AuthContext'
import { Account, GivingProfile, Partner, Region } from '../authContext/AuthContext.types'
import { getSsoHintDomain, removeSsoHint, setSsoHint, ssoLoginEmailForDomain } from '../ssoHint/ssoHint'

import { AccountConfig } from '@percent/workplace-giving/api/account/accountConfig/accountConfig'
import { FullPageSpinner } from '@percent/workplace-giving/common/components/FullPageSpinner/FullPageSpinner'
import { config } from '@percent/workplace-giving/config/config'
import { useClient } from '@percent/workplace-giving/common/hooks'
import { useIdentify } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { useLogin } from '@percent/workplace-giving/common/hooks/useLogin/useLogin'
import { useRateLimit } from '@percent/workplace-giving/common/hooks/useRateLimit/useRateLimit'
import { AuthContextControllerProps } from './AuthContextController.types'

const DEFAULT_STATE = {
  status: 'first_load' as const
}

const DEFAULT_COUNTRY_CODE = 'AUS'

export type AuthAction =
  | {
      type: 'RELOAD'
    }
  | {
      type: 'LOADING'
    }
  | {
      type: 'LOGOUT'
    }
  | {
      type: 'LOGIN'
      payload: {
        permissions: string[]
        account: Account
        partner: Partner
        countryCode?: Alpha3Code
        timeZone?: string
        stateCode?: string | null
        acceptedTerms: string[]
        accConfig?: AccountConfig
        accountCountryCode: Alpha3Code | null
        regions: Region[] | null
        datePeriod?: {
          currentPeriodStarted: Date
        }
        givingProfile?: GivingProfile
      }
    }
  | {
      type: 'UPDATE'
      payload: {
        account: Account
        partner: Partner
        countryCode?: string
        timeZone?: string
        stateCode?: string | null
        acceptedTerms: string[]
        accConfig?: AccountConfig
        givingProfile?: GivingProfile
      }
    }
  | {
      type: 'UPDATE_GIVING_PROFILE'
      payload: {
        givingProfile: GivingProfile
      }
    }

export type AuthorizedAuthState = {
  status: 'authorized'
  permissions: string[]
  account: Account
  partner: Partner
  timeZone?: string
  stateCode?: string | null
  countryCode?: string
  acceptedTerms: string[]
  accConfig?: AccountConfig
  accountCountryCode: Alpha3Code | null
  regions: Region[] | null
  datePeriod: {
    currentPeriodStarted: Date
  }
  givingProfile?: GivingProfile
}
export type AuthState =
  | {
      status: 'first_load'
    }
  | {
      status: 'unauthorized'
    }
  | {
      status: 'loading'
    }
  | AuthorizedAuthState

export function deriveDatePeriod(datePeriod?: { currentPeriodStarted: Date }, currentDate: Date = new Date()) {
  if (!datePeriod?.currentPeriodStarted) {
    const currentYear = currentDate.getFullYear()

    return {
      currentPeriodStarted: new Date(currentYear, 0, 1)
    }
  }

  const presetDate = datePeriod.currentPeriodStarted
  const currentYear = currentDate.getFullYear()
  const presetYear = presetDate.getFullYear()

  if (currentYear - presetYear > 1) {
    const adjustedDate = setYear(presetDate, currentYear - 1)

    return {
      currentPeriodStarted: adjustedDate
    }
  }

  return {
    currentPeriodStarted: presetDate
  }
}

// eslint-disable-next-line consistent-return
function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOADING': {
      return {
        status: 'loading' as const
      }
    }
    case 'LOGIN': {
      return {
        status: 'authorized' as const,
        partner: action.payload.partner,
        account: action.payload.account,
        countryCode:
          (action.payload.countryCode as Alpha3Code) ||
          ((state.status === 'authorized' ? state.countryCode : DEFAULT_COUNTRY_CODE) as Alpha3Code),
        timeZone: action.payload.timeZone,
        stateCode: action.payload.stateCode,
        permissions: action.payload.permissions,
        acceptedTerms: action.payload.acceptedTerms,
        accConfig: action.payload.accConfig,
        accountCountryCode: action.payload.accConfig?.country ?? null,
        regions: action.payload.regions,
        datePeriod: deriveDatePeriod(action.payload.datePeriod),
        givingProfile: action.payload.givingProfile
      }
    }
    case 'LOGOUT': {
      return {
        status: 'unauthorized' as const
      }
    }
    case 'UPDATE': {
      if (state.status === 'authorized') {
        return {
          status: 'authorized' as const,
          partner: action.payload.partner,
          account: action.payload.account,
          countryCode: state.countryCode,
          timeZone: state.timeZone,
          stateCode: state.stateCode,
          permissions: state.permissions,
          acceptedTerms: Array.from(new Set([...state.acceptedTerms, ...action.payload.acceptedTerms])),
          accConfig: {
            ...state.accConfig,
            ...action.payload.accConfig
          },
          accountCountryCode: state.accConfig?.country ?? null,
          regions: state.regions,
          datePeriod: state.datePeriod,
          givingProfile: state.givingProfile
        }
      }

      return state
    }
    case 'UPDATE_GIVING_PROFILE': {
      if (state.status === 'authorized') {
        return {
          ...state,
          givingProfile: {
            ...state.givingProfile,
            ...action.payload.givingProfile
          }
        }
      }

      return state
    }
    case 'RELOAD': {
      return {
        status: 'first_load' as const
      }
    }
  }
}

export const shouldCheckLoggedIn = () => {
  try {
    return localStorage.getItem('auth_check') === 'check'
  } catch {
    return true
  }
}

export const setCheckLoggedIn = () => {
  try {
    localStorage.setItem('auth_check', 'check')
  } catch {}
}

export const removeCheckLoggedIn = () => {
  try {
    localStorage.removeItem('auth_check')
  } catch {}
}

export const setLoginType = (loginType: 'sso' | 'ep') => {
  try {
    localStorage.setItem('login_type', loginType)
  } catch {}

  if (loginType === 'ep') {
    removeSsoHint()
  }
}

export const getLoginType = () => {
  try {
    const loginType = localStorage.getItem('login_type')

    if (loginType && ['sso', 'ep'].includes(loginType)) {
      return loginType as 'sso' | 'ep'
    }

    return null
  } catch {
    return null
  }
}

export const markSsoReauthAttempted = () => {
  try {
    sessionStorage.setItem('sso_reauth_attempted', 'true')
  } catch {}
}

export const clearSsoReauthAttempted = () => {
  try {
    sessionStorage.removeItem('sso_reauth_attempted')
  } catch {}
}

const hasAttemptedSsoReauth = () => {
  try {
    return sessionStorage.getItem('sso_reauth_attempted') === 'true'
  } catch {
    return true
  }
}

const isSsoSession = () => getLoginType() === 'sso' || globalThis.location.pathname === AppRoute.SSO_GATEWAY

const canAttemptSsoReauth = (error: unknown) =>
  error instanceof AxiosError &&
  error.response?.status === 401 &&
  getLoginType() === 'sso' &&
  !hasAttemptedSsoReauth() &&
  globalThis.location.pathname !== AppRoute.SSO_GATEWAY

const AUTH_ROUTES: string[] = [
  AppRoute.SIGN_IN,
  AppRoute.SSO_SIGN_IN,
  AppRoute.OTP_VERIFY,
  AppRoute.FORGOT_PASSWORD,
  AppRoute.SSO_GATEWAY
]

const ssoReauthRedirectUrl = () => {
  const { pathname, search } = globalThis.location

  return AUTH_ROUTES.includes(pathname) ? undefined : config.urls.appUrl + pathname + search
}

export function AuthContextController({
  children,
  defaultState = DEFAULT_STATE
}: Readonly<AuthContextControllerProps>) {
  const [authState, d] = useReducer(reducer, defaultState)
  const { setRateLimitHit } = useRateLimit()
  const { identify } = useIdentify()
  const { theme, setTheme } = useColorTheme()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!shouldCheckLoggedIn() && globalThis.location.pathname !== AppRoute.SSO_GATEWAY) {
      d({ type: 'LOGOUT' })
    }
  }, [])

  const dispatch = useCallback((action: AuthAction) => {
    d(action)

    if (action.type === 'LOGIN') {
      setCheckLoggedIn()
      clearSsoReauthAttempted()

      if (isSsoSession()) {
        setSsoHint(action.payload.account.email)
      }
    }

    if (action.type === 'LOGOUT') {
      removeCheckLoggedIn()
    }
  }, [])

  const value = useMemo(
    () => ({
      state: authState,
      dispatch,
      logout: () => {
        queryClient.clear()
        removeSsoHint()
        clearSsoReauthAttempted()
        dispatch({ type: 'LOGOUT' })
      }
    }),
    [authState, dispatch]
  )

  const { gsClient } = useClient()

  const email = authState?.status === 'authorized' ? authState.account.email : ''

  useEffect(() => {
    let id: number | undefined

    if (email) {
      id = gsClient.interceptors.response.use(
        responseSuccessInterceptor(dispatch, gsClient, email, setRateLimitHit),
        responseFailureInterceptor(dispatch, gsClient, email, setRateLimitHit)
      )
    }

    return () => {
      if (id) {
        gsClient.interceptors.response.eject(id)
      }
    }
  }, [email, dispatch, gsClient, setRateLimitHit])

  const { login } = useLogin({ dispatch })

  const attemptSsoReauth = useCallback(
    async (error: unknown) => {
      const ssoHintDomain = getSsoHintDomain()

      if (!ssoHintDomain || !canAttemptSsoReauth(error)) {
        return false
      }

      try {
        markSsoReauthAttempted()
        dispatch({ type: 'LOADING' })

        const loginUrl = await getSsoLoginUrl(gsClient, ssoLoginEmailForDomain(ssoHintDomain), ssoReauthRedirectUrl())

        if (!loginUrl) {
          return false
        }

        globalThis.location.href = loginUrl

        return true
      } catch {
        return false
      }
    },
    [gsClient, dispatch]
  )

  const secondaryEmail = authState.status === 'authorized' ? authState.givingProfile?.secondaryEmail ?? null : null

  useEffect(() => {
    const getAccountData = async () => {
      try {
        dispatch({ type: 'LOADING' })
        const loginResult = await login({})

        if (loginResult?.rateLimitHit) {
          // Do nothing - rate limit hit, stop processing
        }
      } catch (e) {
        if (!(await attemptSsoReauth(e))) {
          dispatch({ type: 'LOGOUT' })
        }
      }
    }

    if (
      authState.status === 'first_load' &&
      (shouldCheckLoggedIn() || globalThis.location.pathname === AppRoute.SSO_GATEWAY)
    ) {
      getAccountData()
    }

    if (authState.status === 'authorized' && authState.account) {
      identify({
        accountId: authState.account.id,
        traits: {
          createdAt: authState.account.createdAt,
          email: authState.account.email,
          partnerId: authState.partner.id,
          partnerName: authState.partner.name,
          language: 'EN',
          ...(secondaryEmail ? { secondaryEmail } : {})
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.status, secondaryEmail])

  useEffect(() => {
    if (authState.status === 'authorized' && authState.partner?.theme) {
      setTheme({
        ...theme,
        ...authState.partner.theme,
        info30: theme.info30,
        info100: theme.info100,
        info400: theme.info400
      })
    }

    if (authState.status === 'unauthorized') {
      setTheme(BASE_PALETTE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.status])

  if (authState.status === 'loading' || authState.status === 'first_load') {
    return <FullPageSpinner />
  }

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>
}

const getPropertyFromAuthorizedState =
  <T,>(block: (_: AuthorizedAuthState) => T) =>
  (authState: AuthState) =>
    authState.status === 'authorized' ? block(authState) : null

export const getCountryCodeFromAuthState = getPropertyFromAuthorizedState(
  state => state.accountCountryCode ?? (state.countryCode as Alpha3Code)
)

export const getAccountFromAuthState = getPropertyFromAuthorizedState(state => state.account)
export const getPartnerFromAuthState = getPropertyFromAuthorizedState(state => state.partner)
export const getAccountCountryCodeFromAuthState = getPropertyFromAuthorizedState(state => state.accountCountryCode)
export const getTimeZoneFromAuthState = getPropertyFromAuthorizedState(state => state.timeZone)
export const getStateCodeFromAuthState = getPropertyFromAuthorizedState(state => state.stateCode)
export const getAcceptedTermsFromAuthState = getPropertyFromAuthorizedState(state => state.acceptedTerms)
export const getAccountConfigFromAuthState = getPropertyFromAuthorizedState(state => state.accConfig)
export const getRegionsFromAuthState = getPropertyFromAuthorizedState(state => state.regions)
export const getGivingProfileFromAuthState = getPropertyFromAuthorizedState(state => state.givingProfile)
